import { identity } from 'rxjs';
import { cleanListItemElements } from '../../helpers/cleanListItemElements';
import { isFileDataTransfer } from '../../helpers/dataTransfer';
import { clipSelection } from '../../helpers/elementHtmlEditor/clipboard';
import { flattenParagraphs } from '../../helpers/flattenParagraphs';
import { simplifyStyleInRange } from '../../helpers/style/simplifyStyleInRange';
import { createElementBlocksAsYouTypeService } from '../../services/dom/elementBlocksAsYouTypeService';
import { createElementParagraphTextUnderCursorService } from '../../services/dom/elementParagraphTextUnderCursorService';
import { createElementTextUnderCursorService } from '../../services/dom/elementTextUnderCursorService';
import { createHtmlAutolinkService } from '../../services/html/htmlAutolinkService';
import { createHtmlBlockquoteService } from '../../services/html/htmlBlockquoteService';
import { createHtmlImageService } from '../../services/html/htmlImageService';
import { createHtmlLinkService } from '../../services/html/htmlLinkService';
import { createHtmlListService } from '../../services/html/htmlListService';
import { createHtmlParagraphIndentService } from '../../services/html/htmlParagraphIndentService';
import { createHtmlPasteLinkService } from '../../services/html/htmlPasteLinkService';
import { createHtmlSpaceIndentService } from '../../services/html/htmlSpaceIndentService';
import { createHtmlTableEffectService } from '../../services/html/table/htmlTableEffectService';
import { preProcessPastedTables } from '../../services/html/table/preProcessPastedTables';
import { createElementStyleService } from '../../services/rich/elementStyleService';
import { createAutocompleteService } from '../../services/text/autocompleteService';
import { createCombinationIndentService } from '../../services/text/indentService';
import {
  ContentEditableEditor,
  ContentEditableElement,
  createContentEditableEditorSpec,
  EditorTrace,
} from '../base/contentEditableEditor';
import { EditorSpec } from '../base/editor';
import { BaseConnectingHtmlEditor, HtmlEditor, HtmlEditorServices } from './htmlEditor';
import { ContentEditableHtmlEditorPlugin } from './plugins/contentEditableHtmlEditorPlugin';
import { ContentEditableHtmlEditorPluginCollection } from './plugins/contentEditableHtmlEditorPluginCollection';

const SIMPLIFY_STYLES_MAX_HTML_LENGTH = 512 * 1024;
const FLATTEN_MAX_PARAGRAPH_COUNT = 100;

export interface ContentEditableHtmlEditor<S extends HtmlEditorServices = HtmlEditorServices>
  extends ContentEditableEditor<S>,
    HtmlEditor<S> {}

export type ContentEditableHtmlEditorOptions = {
  initialHtml?: string;
  isInitialHtmlClean?: boolean;
  simplifyStyles?: boolean;
  newListClassName?: string;
  shouldPasteAsText?: boolean;
  areStyleKeyboardShortcutsDisabled?: boolean;
  plugins?: ReadonlyArray<ContentEditableHtmlEditorPlugin>;
  shouldNativeDelete?: boolean;
  onTrace?: (trace: EditorTrace) => void;
  hasTableService?: boolean;
  hasNewDeleteSelection?: boolean;
  hasParagraphSpaceIndent?: boolean;
  getHasListAutoFormatting?: () => boolean;
  hasTextDirectionService?: boolean;
  hasListServiceV2?: boolean;
};

type ContentEditableHtmlEditorSpecCreator<S extends HtmlEditorServices> = (
  spec: EditorSpec<HtmlEditorServices>,
) => EditorSpec<S>;

export function createContentEditableHtmlEditor<S extends HtmlEditorServices>(
  options: ContentEditableHtmlEditorOptions,
  createSpec: ContentEditableHtmlEditorSpecCreator<S>,
): ContentEditableHtmlEditor<S>;
export function createContentEditableHtmlEditor(
  options?: ContentEditableHtmlEditorOptions,
): ContentEditableHtmlEditor;
export function createContentEditableHtmlEditor<S extends HtmlEditorServices = HtmlEditorServices>(
  options: ContentEditableHtmlEditorOptions = {},
  createSpec: ContentEditableHtmlEditorSpecCreator<S> = identity as ContentEditableHtmlEditorSpecCreator<S>,
): ContentEditableHtmlEditor<S> {
  const { initialHtml, isInitialHtmlClean, simplifyStyles, newListClassName } = options;

  const customPlugins = options.plugins || [];
  const plugins = new ContentEditableHtmlEditorPluginCollection([
    ...customPlugins,
    createCoreAfterPlugin(options),
  ]);

  const editor = new BaseConnectingHtmlEditor<ContentEditableElement, S>(element$ => {
    const baseSpec = createContentEditableEditorSpec(element$, {
      sanitizeHtmlOptions: {
        addAllowedAttributes: plugins.addAllowedAttributes,
      },
      simplifyStylesMaxHtmlLength: SIMPLIFY_STYLES_MAX_HTML_LENGTH,
      didSetHtml: (element, htmlLength) => {
        const document = element.ownerDocument;
        if (!document) {
          return;
        }

        if (simplifyStyles && htmlLength <= SIMPLIFY_STYLES_MAX_HTML_LENGTH) {
          const range = document.createRange();
          range.selectNodeContents(element);
          simplifyStyleInRange(element, range);
        }
      },
      plugins: plugins.plugins,
      shouldNativeDelete: options.shouldNativeDelete,
      onTrace: options.onTrace,
      hasTableService: options.hasTableService,
      hasNewDeleteSelection: options.hasNewDeleteSelection,
      shouldPasteAsTextInCodeBlocks: false,
      hasTextDirectionService: options.hasTextDirectionService,
    });
    const { services: baseServices, privateServices: basePrivateServices = [] } = baseSpec;
    const { selection: selectionService, undo: undoService } = baseServices;

    const textUnderCursorService = createElementTextUnderCursorService(selectionService);
    const paragraphTextUnderCursorService = createElementParagraphTextUnderCursorService(selectionService);

    const styleService = createElementStyleService(element$, selectionService, {
      areKeyboardShortcutsDisabled: options.areStyleKeyboardShortcutsDisabled,
    });
    const linkService = createHtmlLinkService(element$, selectionService);
    const autoLinkService = createHtmlAutolinkService(element$, selectionService);
    const pasteLinkService = createHtmlPasteLinkService(element$, selectionService, linkService);

    const listService = createHtmlListService(element$, selectionService, {
      newListClassName,
      hasListServiceV2: options.hasListServiceV2,
    });
    const blockquoteService = createHtmlBlockquoteService(element$, selectionService);
    const paragraphIndentService = createHtmlParagraphIndentService(element$, selectionService, {
      hasParagraphSpaceIndent: options.hasParagraphSpaceIndent,
    });
    const spaceIndentService = createHtmlSpaceIndentService(
      element$,
      selectionService,
      textUnderCursorService,
      paragraphTextUnderCursorService,
      { hasParagraphSpaceIndent: options.hasParagraphSpaceIndent },
      autoLinkService,
    );
    const indentService = createCombinationIndentService(
      listService,
      blockquoteService,
      paragraphIndentService,
      spaceIndentService,
    );
    const imageService = createHtmlImageService(element$, selectionService, undoService, {
      isImageInteractive: image => plugins.isImageInteractive(image),
    });

    const autocompleteService = createAutocompleteService(textUnderCursorService);

    const elementBlocksAsYouTypeService = createElementBlocksAsYouTypeService(
      element$,
      selectionService,
      paragraphTextUnderCursorService,
      blockquoteService,
      listService,
      undoService,
      { getHasListAutoFormatting: options.getHasListAutoFormatting },
    );

    return createSpec({
      ...baseSpec,
      services: {
        ...baseServices,
        textUnderCursor: textUnderCursorService,
        autocomplete: autocompleteService,
        style: styleService,
        link: linkService,
        indent: indentService,
        quote: blockquoteService,
        list: listService,
        image: imageService,
      },
      privateServices: [
        autoLinkService,
        pasteLinkService,
        elementBlocksAsYouTypeService,
        ...basePrivateServices,
      ].concat(
        options.hasTableService
          ? [createHtmlTableEffectService(element$, selectionService, undoService)]
          : [],
      ),
    });
  });

  if (!initialHtml || isInitialHtmlClean) {
    editor.setCleanHtml(initialHtml || '');
  } else {
    editor.setDirtyHtml(initialHtml);
  }

  return editor;
}

const createCoreAfterPlugin = (
  options: ContentEditableHtmlEditorOptions,
): ContentEditableHtmlEditorPlugin => ({
  processTree: element => {
    flattenParagraphs(element, {
      maxParagraphCount: FLATTEN_MAX_PARAGRAPH_COUNT,
    });
    cleanListItemElements(element);
  },
  handleDataTransfer: (dataTransfer, editService) => {
    // If this appears to be a file upload/drop, then ignore any HTML versions and let the event bubble.
    if (isFileDataTransfer(dataTransfer)) {
      return false;
    }

    const html = dataTransfer.getData('text/html');
    if (html && !options.shouldPasteAsText) {
      editService.insertDirtyHtml(html);
      return true;
    }

    const text = dataTransfer.getData('text/plain');
    if (text) {
      editService.insertText(text);
      return true;
    }

    return false;
  },
  populateDataTransfer: (dataTransfer, element) => {
    const selection = element.ownerDocument && element.ownerDocument.getSelection();
    const clipping = clipSelection(element, selection, {
      hasTableService: options.hasTableService,
    });
    if (!clipping) {
      return false;
    }

    dataTransfer.setData('text/html', clipping.html);
    dataTransfer.setData('text/plain', clipping.text);
    return true;
  },
  preProcessInsertedHtml: (dirtyHtml: string, document?: Document) => {
    if (!document) {
      return dirtyHtml;
    }
    return preProcessPastedTables(dirtyHtml, document);
  },
});
